import paper from 'paper';

export function randomId() {
  return (
    Date.now().toString() +
    Math.round(Math.random() * 1000000)
      .toString()
      .padStart(6, '0')
  );
}

export type ArcArrowStyles = 'inlineHead' | 'inlineBoth' | 'hat';

// Generate an SVG arrow that follows an arc.
export function arcArrowPath(
  x: number,
  y: number,
  startAngle: number,
  endAngle: number,
  radius: number,
  thickness: number,
  arrowStyle: ArcArrowStyles
) {
  // Calculate the outer and inner radius
  const outerRadius = radius + thickness / 2;
  const innerRadius = radius - thickness / 2;

  // Helper for calculating points on a circle.
  const circularPoint = (radius: number, angle: number) => {
    return {
      x: x + radius * Math.cos(angle),
      y: y + radius * Math.sin(angle),
    };
  };

  let arrowRadiusOutset = 0; // How much the arrow extends in or out from the cirlce.
  let arrowExtension = 0; // How much further around the circle the arrow extends.
  switch (arrowStyle) {
    case 'inlineHead':
    case 'inlineBoth':
      arrowExtension = thickness / 2;
      break;
    case 'hat':
      arrowRadiusOutset = thickness / 2;
      arrowExtension = thickness;
      break;
    default:
      throw new Error(`Unknown arrow style: ${arrowStyle}`);
  }
  const arrowExtensionAngle = Math.atan(arrowExtension / radius);

  // Calculate start and end points for arcs.
  endAngle -= arrowExtensionAngle;
  const innerStart = circularPoint(innerRadius, startAngle);
  const innerEnd = circularPoint(innerRadius, endAngle);
  const outerStart = circularPoint(outerRadius, startAngle);
  const outerEnd = circularPoint(outerRadius, endAngle);

  // Large arc flag and sweep flag for outer and inner arcs
  const largeArcFlag = endAngle - startAngle <= Math.PI ? '0' : '1';

  let pathData: string = '';

  switch (arrowStyle) {
    case 'inlineHead':
    case 'inlineBoth': {
      const i1 = circularPoint(radius, endAngle + arrowExtensionAngle);
      const i2 = circularPoint(radius, startAngle + arrowExtensionAngle);

      // Construct the SVG path data
      pathData = `
        M ${outerStart.x},${outerStart.y}
        A ${outerRadius},${outerRadius} 0 ${largeArcFlag} 1 ${outerEnd.x},${outerEnd.y}
        L ${outerEnd.x},${outerEnd.y}
        L ${i1.x},${i1.y}
        L ${innerEnd.x},${innerEnd.y}
        A ${innerRadius},${innerRadius} 0 ${largeArcFlag} 0 ${innerStart.x},${innerStart.y}
        L ${i2.x},${i2.y}
        Z
      `;
      break;
    }

    case 'hat': {
      // Calculate the points for the arrowhead
      const h1 = circularPoint(outerRadius + arrowRadiusOutset, endAngle);
      const h2 = circularPoint(radius, endAngle + arrowExtensionAngle);
      const h3 = circularPoint(innerRadius - arrowRadiusOutset, endAngle);

      // Construct the SVG path data
      pathData = `
        M ${outerStart.x},${outerStart.y}
        A ${outerRadius},${outerRadius} 0 ${largeArcFlag} 1 ${outerEnd.x},${outerEnd.y}
        L ${outerEnd.x},${outerEnd.y}
        L ${h1.x},${h1.y}
        L ${h2.x},${h2.y}
        L ${h3.x},${h3.y}
        L ${innerEnd.x},${innerEnd.y}
        A ${innerRadius},${innerRadius} 0 ${largeArcFlag} 0 ${innerStart.x},${innerStart.y}
        Z
      `;
      break;
    }
  }

  return pathData.trim();
}

// Given an SVG path, translate it so that the bounding box starts at 0,0 and
// return as a paper path.
export function normalizeSvgPath(pathData: string): {
  path: paper.Path;
  left: number;
  top: number;
} {
  // make sure paper is set up.
  if (!paper.project) {
    // @ts-expect-error Paper's types are wrong, it's valid to call this with no arguments - see Project constructor
    paper.setup();
  }

  const path = new paper.Path(pathData);
  const bounds = path.bounds;
  path.translate(
    new paper.Point(
      -bounds.left - bounds.width / 2,
      -bounds.top - bounds.height / 2
    )
  );

  return {
    path,
    left: bounds.left,
    top: bounds.top,
  };
}

// Compute the angles of the intersections points between a circle and a
// rectangle.
export function computeIntersectionAngles(
  cx: number,
  cy: number,
  r: number,
  rectWidth: number,
  rectHeight: number,
  angle: number
): number[] {
  // Calculate the center of the rectangle
  const rx = cx + r * Math.cos(angle);
  const ry = cy + r * Math.sin(angle);

  // Vertices of the rectangle
  const halfWidth = rectWidth / 2;
  const halfHeight = rectHeight / 2;

  const vertices = [
    { x: rx - halfWidth, y: ry - halfHeight },
    { x: rx + halfWidth, y: ry - halfHeight },
    { x: rx + halfWidth, y: ry + halfHeight },
    { x: rx - halfWidth, y: ry + halfHeight },
  ];

  // Line equations for the edges of the rectangle
  const edges = [
    {
      x1: vertices[0].x,
      y1: vertices[0].y,
      x2: vertices[1].x,
      y2: vertices[1].y,
    },
    {
      x1: vertices[1].x,
      y1: vertices[1].y,
      x2: vertices[2].x,
      y2: vertices[2].y,
    },
    {
      x1: vertices[2].x,
      y1: vertices[2].y,
      x2: vertices[3].x,
      y2: vertices[3].y,
    },
    {
      x1: vertices[3].x,
      y1: vertices[3].y,
      x2: vertices[0].x,
      y2: vertices[0].y,
    },
  ];

  const intersections: { x: number; y: number }[] = [];

  // Helper function to solve the quadratic equation
  function solveQuadratic(a: number, b: number, c: number): number[] {
    const discriminant = b * b - 4 * a * c;
    if (discriminant < 0) return [];
    const sqrtDiscriminant = Math.sqrt(discriminant);
    return [
      (-b + sqrtDiscriminant) / (2 * a),
      (-b - sqrtDiscriminant) / (2 * a),
    ];
  }

  // Find intersections of the rectangle edges with the circle
  for (const edge of edges) {
    const { x1, y1, x2, y2 } = edge;
    const dx = x2 - x1;
    const dy = y2 - y1;

    // Parametric form of the line equation
    const a = dx * dx + dy * dy;
    const b = 2 * (dx * (x1 - cx) + dy * (y1 - cy));
    const c = (x1 - cx) * (x1 - cx) + (y1 - cy) * (y1 - cy) - r * r;

    const tValues = solveQuadratic(a, b, c);

    for (const t of tValues) {
      if (t >= 0 && t <= 1) {
        const intersectionX = x1 + t * dx;
        const intersectionY = y1 + t * dy;
        intersections.push({ x: intersectionX, y: intersectionY });
      }
    }
  }

  return intersections.map(({ x, y }) => {
    let theta = Math.atan2(y - cy, x - cx);
    if (theta < 0) {
      theta += 2 * Math.PI;
    }
    return theta;
  });
}
